import ActionButton from 'components/ActionButton';
import React, { useEffect, useState } from 'react';
import DataTable from 'components/DataTable';
import { fetchAllSupplier } from 'services';
import { DATE_FORMATS, formatDate, getPageLimit, showError, updatePageLimit } from 'utils';
import { tableStyles } from '../../../css/tableStyles';
import Filter from './Filter';
import styles from './index.module.scss';
import qs from 'query-string';
import CustomLink from 'components/TableLink';

let filters: { [p: string]: any; page: number } = {
  page: 0,
};

const columns = [
  {
    name: 'Supplier ID',
    selector: 'objectId',
    sortable: false,
    // eslint-disable-next-line react/display-name
    cell: row => (
      <CustomLink
        text={row.objectId}
        linkTo={`/suppliers/${row.objectId}`}
      />
    ),
  },
  {
    name: 'Created At',
    selector: 'createdAt',
    sortable: true,
    format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
  },
  {
    name: 'Name',
    selector: 'name',
    sortable: true,
    // eslint-disable-next-line react/display-name
    cell: row => (
      <div className={styles['producer-name-and-avatar']}>
        {!!row.profilePhoto && <img src={row.profilePhoto._url} />}
        <div>{row.name}</div>
      </div>
    ),
  },
  {
    name: 'Email',
    selector: 'email',
  },
  {
    name: 'Phone',
    selector: 'phone',
    format: (row) => row.phone ? row.phone : '-',
  },
  {
    name: 'Featured',
    selector: 'featured',
    // eslint-disable-next-line react/display-name
    cell: row => (
      <div>{row.featured?.toString() || 'False'}</div>
    ),
  },
  {
    name: 'Visible',
    selector: 'visible',
    // eslint-disable-next-line react/display-name
    cell: row => (
      <div>{row.visible?.toString() || 'True'}</div>
    ),
  },
];

const Suppliers = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [suppliers, setSuppliers] = useState<Record<string, any>>({});

  const fetchAllSuppliers = async () => {
    setLoading(true);
    try {
      const response = await fetchAllSupplier({
        ...filters,
        limit: getPageLimit(),
      });
      setSuppliers(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!props.data) {
      fetchAllSuppliers();
    }
  }, []);

  const onSort = (column: Record<string, any>, sortDirection: string) => {
    filters = {
      ...filters,
      sort: {
        ...filters.sort,
        [column.selector]: sortDirection,
      },
    };
    fetchAllSuppliers();
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    updateQueryString();
    fetchAllSuppliers();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const filterToQueryString = () => {
    const { filter } = filters;
    return qs.stringify({ page: filters.page, ...filter });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    if (updatedFilter.objectId) {
      props.history.push(`/suppliers/${updatedFilter.objectId}`);
    } else {
      filters = {
        ...filters,
        page: 0,
        filter: {
          ...updatedFilter,
        },
      };
      updateQueryString();
      fetchAllSuppliers();
    }
  };

  return (
    <>
      {!props.data && (
        <div className={styles['header']}>
          <div className={styles['heading']}>Suppliers</div>
          <ActionButton
            text={<>Supplier <span>+</span></>}
            active={true}
            isLoading={false}
            onClick={() => props.history.push('/suppliers/create')}
          />
        </div>
      )}
      {!props.data && (
        <Filter {...props} onSubmit={onFilterSubmit} />
      )}
      <DataTable
        keyField="objectId"
        columns={columns}
        noHeader={true}
        data={props.data || suppliers.items}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={props.total || suppliers.total}
        paginationRowsPerPageOptions={[50, 100, 200]}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        sortServer={true}
        onSort={onSort}
        loading={loading}
      />
    </>
  );
};

export { Suppliers };
